import * as Sentry from '@sentry/react';
import config from 'config';
import { scheduleConsultationFromSignupV2 } from 'domains/consultations/freeConsultationSchedule';
import { DirectSignupFilters } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import {
  createSalesforceLead,
  updateSalesforceLead,
  upsertSalesforceInsuranceLead,
} from 'domains/signupv4/Leads/queries';
import { getTimezoneOptionFromResolved, ResolvedTimeZone, TimezoneOption } from 'domains/timezone';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import mixpanel from 'mixpanel-browser';
import { ConsultationFormType } from '../schemas/consultationSchema';
import { mergeCommunicationConcerns } from 'domains/signupDirect/misc/communicationConcerns';
import { getSFInsuranceType } from 'domains/signupv4/Parent/actors';
import { PhysicianForm } from 'domains/signup/data/types';
import { toStateFull, StateAbbrev } from 'utils/states';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';

export const submitConsultationForm = async (
  data: ConsultationFormType,
  filters: DirectSignupFilters,
  isResumedLead: boolean = false,
) => {
  let primaryPhysician: PhysicianForm | undefined;

  // needs type enhancement, copied from submitEvalForm
  if (data.billingInfo.type === 'insurance-medicaid' || data.billingInfo.type === 'insurance-medicare') {
    primaryPhysician = {
      name: data.billingInfo.primaryPhysician.name || '',
      office: data.billingInfo.primaryPhysician.officeName || '',
      phoneNumber: data.billingInfo.primaryPhysician.phone || '',
      website: data.billingInfo.primaryPhysician.website || '',
    } as PhysicianForm;
  }

  try {
    const UTMParams = getUTMParameters();
    const GCLIDParams = getGCLIDParameters();

    const { clientInfo, contactInfo, consultSchedule } = data;

    // create lead
    const leadResponse = await createSalesforceLead({
      address: {
        state: filters.stateCode,
      },
      email: contactInfo.email,
      firstName: clientInfo.firstName,
      lastName: clientInfo.lastName,
      phone: contactInfo.phone,
      signupFlowOutcome: 'Booked for Consultation',
      sourceSystem: 'Signup Direct',
      contactTimezone: data.consultSchedule.timeZone,
      ...UTMParams,
      ...GCLIDParams,
    });

    // Update to add some properties that aren't available on the schedule endpoint @TODO
    await updateSalesforceLead({
      detectedAreas: data.concerns.clinicalAreas,
      id: leadResponse.id,
      schedulingId: leadResponse.schedulingId,
      desiredPaymentMethod: filters.paymentMethod.type === 'privatePay' ? 'self-pay' : 'Insurance',
      communicationConcerns: mergeCommunicationConcerns(data.concerns.clinicalConcernKeys, data.concerns.clinicalAreas),
      selfSignUp: false,
      address: {
        state: data.clientInfo.stateCode,
      },
      firstName: data.clientInfo.firstName,
      lastName: data.clientInfo.lastName,
      email: data.contactInfo.email,
      dob: data.clientInfo.dateOfBirth,
      phone: data.contactInfo.phone,
      serviceFor: data.clientType,
      primaryLanguage: filters.language,
      relationshipToClient: data.contactInfo.relationshipToClient,
      // needs type enhancement, copied from submitEvalForm
      insuranceType:
        filters.paymentMethod.type === 'insurance'
          ? getSFInsuranceType(filters.paymentMethod.insuranceData.insuranceType, false)
          : undefined,
      memberID:
        filters.paymentMethod.type === 'insurance' && data.billingInfo && 'memberId' in data.billingInfo
          ? data.billingInfo.memberId
          : undefined,
      payerPlanName:
        filters.paymentMethod.type === 'insurance' && data.billingInfo && 'insuranceData' in data.billingInfo
          ? data.billingInfo.insuranceData.candidPayerName
          : undefined,
      payerForClaims:
        filters.paymentMethod.type === 'insurance' && data.billingInfo && 'insuranceData' in data.billingInfo
          ? data.billingInfo.insuranceData.payerId
          : undefined,
      primaryPhysician,
      subscriber:
        filters.paymentMethod.type === 'insurance' && data.billingInfo && 'subscriber' in data.billingInfo
          ? {
              firstName: data.billingInfo.subscriber.firstName || '',
              lastName: data.billingInfo.subscriber.lastName || '',
              dateOfBirth: new Date(data.billingInfo.subscriber.dateOfBirth),
              relationshipToClient: SUBSCRIBER_RELATIONSHIP_CODE_MAP[data.billingInfo.subscriber.relationShipToClient],
            }
          : undefined,
      coPayAmount:
        filters.paymentMethod.type === 'insurance' && data.billingInfo && 'insuranceData' in data.billingInfo
          ? data.billingInfo.insuranceData.averageSessionPatientResponsibility ||
            data.billingInfo.insuranceData.defaultDeposit
          : undefined,
    });

    // needs type enhancement, copied from submitEvalForm
    if (data.billingInfo.type !== 'privatePay' && 'insuranceData' in data.billingInfo) {
      await upsertSalesforceInsuranceLead({
        insuranceLeadID: null,
        leadID: leadResponse.id,
        insuranceType: getSFInsuranceType(data.billingInfo.insuranceData.insuranceType, false),
        payerPlanName: data.billingInfo.insuranceData.insuranceName,
        payerNameClaims: data.billingInfo.insuranceData.candidPayerName,
        payerState: data.billingInfo.insuranceData.state
          ? toStateFull(data.billingInfo.insuranceData.state as StateAbbrev)
          : undefined,
        payerForClaims: data.billingInfo.insuranceData.payerId,
        memberID: data.billingInfo.memberId || '',
        subscriber:
          filters.paymentMethod.type === 'insurance' && data.billingInfo && 'subscriber' in data.billingInfo
            ? {
                firstName: data.billingInfo.subscriber.firstName || '',
                lastName: data.billingInfo.subscriber.lastName || '',
                dateOfBirth: new Date(data.billingInfo.subscriber.dateOfBirth),
                relationshipToClient:
                  SUBSCRIBER_RELATIONSHIP_CODE_MAP[data.billingInfo.subscriber.relationShipToClient],
              }
            : undefined,
        coPayAmount:
          data.billingInfo.insuranceData.averageSessionPatientResponsibility ||
          data.billingInfo.insuranceData.defaultDeposit,
      });
    }

    // schedule consultation
    await scheduleConsultationFromSignupV2({
      consultationDate: consultSchedule.time,
      consultationTimezone: (
        getTimezoneOptionFromResolved(consultSchedule.timeZone as ResolvedTimeZone) as TimezoneOption
      ).value,
      schedulingId: leadResponse.schedulingId,
      appointmentType: 'initial-consult',
    });

    mixpanel.track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.CONSULT.SUBMITTED, { isResumedLead });

    window.location.href = config.get('marketingThankYouURL') as string;
  } catch (error) {
    Sentry.captureException(new Error('[SignupConsolidated] :: Error submitting consultation form'), {
      data: {
        error,
        filters,
        data,
      },
    });
    throw error;
  }

  // Workaround to keep the form submit "loading" while the slow redirect url loads @TODO can probably do a loading screen for both flows
  await new Promise(() => {});
};
